// TopUpModal — credit packs purchase modal
// Shows available packs and initiates Stripe checkout for one-time payments.

import { useState } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { useTopup } from "../../hooks/useCredits";
import { formatCents } from "../../lib/utils";
import type { TopupPack } from "../../../../shared/types";

interface TopUpModalProps {
  open: boolean;
  onClose: () => void;
}

const PACKS: { id: TopupPack; credits: number; price_cents: number; label: string; savings?: string }[] = [
  { id: "10", credits: 10, price_cents: 800, label: "Starter", savings: undefined },
  { id: "30", credits: 30, price_cents: 2000, label: "Standard", savings: "Save 17%" },
  { id: "100", credits: 100, price_cents: 5000, label: "Pro", savings: "Save 38%" },
];

export function TopUpModal({ open, onClose }: TopUpModalProps) {
  const [selectedPack, setSelectedPack] = useState<TopupPack>("30");
  const [error, setError] = useState<string | null>(null);
  const topup = useTopup();

  async function handlePurchase() {
    setError(null);
    try {
      const result = await topup.mutateAsync({
        pack: selectedPack,
        success_url: `${window.location.origin}/usage?topup=success`,
        cancel_url: `${window.location.origin}/usage?topup=cancelled`,
      });

      const checkoutUrl = result.data?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        setError("Failed to get checkout URL. Please try again.");
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to start checkout";
      setError(message);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Buy Credits">
      <div className="space-y-3">
        {PACKS.map((pack) => {
          const isSelected = selectedPack === pack.id;
          const perCredit = pack.price_cents / pack.credits;

          return (
            <button
              key={pack.id}
              onClick={() => setSelectedPack(pack.id)}
              className={`w-full rounded-lg border-2 p-4 text-left transition-colors ${
                isSelected
                  ? "border-brand-600 bg-brand-50"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">
                      {pack.credits} credits
                    </span>
                    {pack.savings && (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                        {pack.savings}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {formatCents(Math.round(perCredit))} per credit
                  </p>
                </div>
                <span className="text-lg font-bold text-slate-900">
                  {formatCents(pack.price_cents)}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <p className="mt-3 text-center text-sm text-red-600">{error}</p>
      )}

      <Button
        className="mt-4 w-full"
        size="lg"
        onClick={handlePurchase}
        loading={topup.isPending}
      >
        Purchase {PACKS.find((p) => p.id === selectedPack)?.credits} Credits
      </Button>

      <p className="mt-3 text-center text-xs text-slate-400">
        One-time payment. Credits never expire.
      </p>
    </Modal>
  );
}
